import { useState, useCallback, useEffect, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { NoteEditor } from "@/components/notes/note-editor";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";

function getCsrfHeaders(): Record<string, string> {
  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split("=");
    if (name === "XSRF-TOKEN") {
      return { "X-XSRF-TOKEN": decodeURIComponent(value) };
    }
  }
  return {};
}
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Save, StickyNote, Pencil, X, Lock, Check, Loader2, CircleDot, CloudUpload, Maximize2, Share, Clipboard, FileDown, Type } from "lucide-react";
import { formatNoteMarkdown, generateNotePdf, copyNoteFormatted } from "@/lib/note-export";

const LOCK_POLL_INTERVAL = 15000;
const LOCK_HEARTBEAT_INTERVAL = 60000;
const AUTO_SAVE_DELAY = 2500;

type SaveStatus = "idle" | "unsaved" | "saving" | "saved" | "error";

interface NoteData {
  id: string | null;
  entityType: string;
  entityId: string;
  content: string;
  updatedAt: string | null;
  createdAt: string | null;
  updatedBy: { id: string; username: string } | null;
  lockedBy: { id: string; username: string | null } | null;
  lockedAt: string | null;
}

interface EntityNotesSectionProps {
  entityType: "task" | "animal_profile";
  entityId: string;
  canEdit: boolean;
  currentUserId?: string;
  hideLabel?: boolean;
  onExpand?: () => void;
  shareLabel?: string;
}

function formatTimeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function hasContent(html: string): boolean {
  const stripped = html.replace(/<[^>]*>/g, "").trim();
  return stripped.length > 0;
}

function SaveStatusIndicator({ status }: { status: SaveStatus }) {
  switch (status) {
    case "unsaved":
      return (
        <span className="flex items-center gap-1 text-xs text-amber-500 dark:text-amber-400" data-testid="text-save-status-unsaved">
          <CircleDot className="w-3 h-3" />
          Unsaved changes
        </span>
      );
    case "saving":
      return (
        <span className="flex items-center gap-1 text-xs text-muted-foreground" data-testid="text-save-status-saving">
          <Loader2 className="w-3 h-3 animate-spin" />
          Saving…
        </span>
      );
    case "saved":
      return (
        <span className="flex items-center gap-1 text-xs text-emerald-500 dark:text-emerald-400" data-testid="text-save-status-saved">
          <Check className="w-3 h-3" />
          Saved
        </span>
      );
    case "error":
      return (
        <span className="flex items-center gap-1 text-xs text-destructive" data-testid="text-save-status-error">
          <X className="w-3 h-3" />
          Save failed
        </span>
      );
    default:
      return null;
  }
}

export function EntityNotesSection({ entityType, entityId, canEdit, currentUserId, hideLabel = false, onExpand, shareLabel }: EntityNotesSectionProps) {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [isEditing, setIsEditing] = useState(false);
  const [pendingContent, setPendingContent] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [navGuardTarget, setNavGuardTarget] = useState<string | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const isEditingRef = useRef(false);
  const isDirtyRef = useRef(false);
  const pendingContentRef = useRef<string | null>(null);
  const savedContentRef = useRef<string>("");
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedStatusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const editVersionRef = useRef(0);
  const savingRef = useRef(false);
  const pendingSaveRef = useRef<{ content: string; keepLock: boolean } | null>(null);

  const baseUrl = `/api/notes/${entityType}/${encodeURIComponent(entityId)}`;

  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  useEffect(() => {
    pendingContentRef.current = pendingContent;
  }, [pendingContent]);

  const noteQueryKey = ["/api/notes", entityType, entityId];

  const { data, isLoading } = useQuery<{ note: NoteData | null }>({
    queryKey: noteQueryKey,
    enabled: !!entityId,
    refetchInterval: isEditing ? false : LOCK_POLL_INTERVAL,
  });

  useEffect(() => {
    savedContentRef.current = data?.note?.content ?? "";
  }, [data]);

  const lockMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("POST", `${baseUrl}/lock`);
    },
  });

  const unlockMutation = useMutation({
    mutationFn: async () =>
      apiRequest("DELETE", `${baseUrl}/lock`),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: noteQueryKey });
    },
  });

  const triggerSaveRef = useRef<(content: string, keepLock: boolean) => void>(() => {});

  const saveMutation = useMutation({
    mutationFn: async ({ content, keepLock }: { content: string; keepLock: boolean; version: number }) =>
      apiRequest("PUT", baseUrl, { content, keepLock }),
    onSuccess: (_data, variables) => {
      savingRef.current = false;
      queryClient.invalidateQueries({ queryKey: noteQueryKey });

      const hasNewerEdits = editVersionRef.current > variables.version;

      if (!hasNewerEdits) {
        setIsDirty(false);
        isDirtyRef.current = false;
        setSaveStatus("saved");
        if (savedStatusTimerRef.current) clearTimeout(savedStatusTimerRef.current);
        savedStatusTimerRef.current = setTimeout(() => setSaveStatus("idle"), 3000);
      }

      if (!variables.keepLock && !hasNewerEdits) {
        setPendingContent(null);
        setIsEditing(false);
        isEditingRef.current = false;
      }

      if (pendingSaveRef.current) {
        const queued = pendingSaveRef.current;
        pendingSaveRef.current = null;
        triggerSaveRef.current(queued.content, queued.keepLock);
      }
    },
    onError: (error: Error) => {
      savingRef.current = false;
      setSaveStatus("error");
      toast({ title: "Failed to save note", description: error.message, variant: "destructive" });

      if (pendingSaveRef.current) {
        const queued = pendingSaveRef.current;
        pendingSaveRef.current = null;
        triggerSaveRef.current(queued.content, queued.keepLock);
      }
    },
  });

  triggerSaveRef.current = (content: string, keepLock: boolean) => {
    if (savingRef.current) {
      pendingSaveRef.current = { content, keepLock };
      return;
    }
    savingRef.current = true;
    const versionAtSave = editVersionRef.current;
    setSaveStatus("saving");
    saveMutation.mutate({ content, keepLock, version: versionAtSave });
  };

  const handleUpdate = useCallback((html: string) => {
    const normalize = (s: string) => (s === "<p></p>" ? "" : s);
    if (normalize(html) === normalize(savedContentRef.current)) return;

    setPendingContent(html);
    pendingContentRef.current = html;
    editVersionRef.current += 1;
    setIsDirty(true);
    isDirtyRef.current = true;
    setSaveStatus("unsaved");

    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      const currentContent = pendingContentRef.current;
      if (currentContent !== null && isDirtyRef.current) {
        triggerSaveRef.current(currentContent, true);
      }
    }, AUTO_SAVE_DELAY);
  }, []);

  const handleManualSave = useCallback(() => {
    if (pendingContent !== null && isDirty) {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      triggerSaveRef.current(pendingContent, true);
    }
  }, [pendingContent, isDirty]);

  const handleDone = useCallback(() => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);

    if (isDirty && pendingContent !== null) {
      triggerSaveRef.current(pendingContent, false);
    } else {
      setIsEditing(false);
      isEditingRef.current = false;
      setPendingContent(null);
      setIsDirty(false);
      isDirtyRef.current = false;
      setSaveStatus("idle");
      unlockMutation.mutate();
    }
  }, [isDirty, pendingContent, unlockMutation]);

  const handleStartEditing = useCallback(async () => {
    try {
      const result = await lockMutation.mutateAsync();
      if (result.acquired) {
        setPendingContent(null);
        setIsDirty(false);
        isDirtyRef.current = false;
        setSaveStatus("idle");
        setIsEditing(true);
        isEditingRef.current = true;
      } else {
        const holderName = result.lock?.username ?? "another user";
        toast({
          title: "Cannot edit",
          description: `Currently being edited by ${holderName}`,
          variant: "destructive",
        });
        queryClient.invalidateQueries({ queryKey: noteQueryKey });
      }
    } catch {
      toast({
        title: "Failed to acquire edit lock",
        variant: "destructive",
      });
    }
  }, [lockMutation, noteQueryKey, toast]);

  const handleCancelEditing = useCallback(() => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    setPendingContent(null);
    setIsDirty(false);
    isDirtyRef.current = false;
    setSaveStatus("idle");
    setIsEditing(false);
    isEditingRef.current = false;
    unlockMutation.mutate();
  }, [unlockMutation]);

  const handleExpandRequest = useCallback(() => {
    if (!isEditing) {
      onExpand?.();
    } else if (!isDirty) {
      handleCancelEditing();
      onExpand?.();
    } else {
      setNavGuardTarget("__expand__");
    }
  }, [isEditing, isDirty, handleCancelEditing, onExpand]);

  useEffect(() => {
    if (!isEditing) return;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirtyRef.current) {
        e.preventDefault();
        return "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isEditing]);

  useEffect(() => {
    if (!isEditing) return;

    const originalPushState = history.pushState.bind(history);
    const originalReplaceState = history.replaceState.bind(history);

    const interceptNavigation = (url?: string | URL | null) => {
      if (isDirtyRef.current && url) {
        const target = typeof url === "string" ? url : url.toString();
        setNavGuardTarget(target);
        return true;
      }
      return false;
    };

    history.pushState = function (data: unknown, unused: string, url?: string | URL | null) {
      if (interceptNavigation(url)) return;
      return originalPushState(data, unused, url);
    };

    history.replaceState = function (data: unknown, unused: string, url?: string | URL | null) {
      if (interceptNavigation(url)) return;
      return originalReplaceState(data, unused, url);
    };

    const handlePopState = (e: PopStateEvent) => {
      if (isDirtyRef.current) {
        e.preventDefault();
        history.pushState(null, "", window.location.href);
        setNavGuardTarget("__back__");
      }
    };
    window.addEventListener("popstate", handlePopState);

    return () => {
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
      window.removeEventListener("popstate", handlePopState);
    };
  }, [isEditing]);

  const handleNavConfirm = useCallback(() => {
    const target = navGuardTarget;
    setNavGuardTarget(null);

    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    setPendingContent(null);
    setIsDirty(false);
    isDirtyRef.current = false;
    setSaveStatus("idle");
    setIsEditing(false);
    isEditingRef.current = false;

    fetch(`${baseUrl}/lock`, {
      method: "DELETE",
      credentials: "include",
      headers: getCsrfHeaders(),
    }).catch(() => {});
    queryClient.invalidateQueries({ queryKey: noteQueryKey });

    if (target === "__expand__") {
      setTimeout(() => onExpand?.(), 0);
    } else if (target && target !== "__back__") {
      setTimeout(() => setLocation(target), 0);
    } else if (target === "__back__") {
      setTimeout(() => history.back(), 0);
    }
  }, [navGuardTarget, baseUrl, noteQueryKey, setLocation, onExpand]);

  const handleNavCancel = useCallback(() => {
    setNavGuardTarget(null);
  }, []);

  const handleCopyMarkdown = useCallback(async () => {
    const html = savedContentRef.current || "";
    const label = shareLabel || "Notes";
    const md = formatNoteMarkdown(html, label);
    try {
      await navigator.clipboard.writeText(md);
      setShareOpen(false);
      toast({ title: "Copied", description: "Note copied as Markdown." });
    } catch {
      toast({ variant: "destructive", title: "Copy failed", description: "Unable to copy to clipboard." });
    }
  }, [shareLabel, toast]);

  const handleDownloadPdf = useCallback(() => {
    const html = savedContentRef.current || "";
    const label = shareLabel || "Notes";
    generateNotePdf(html, label);
    setShareOpen(false);
    toast({ title: "PDF downloaded", description: "Note saved as PDF." });
  }, [shareLabel, toast]);

  const handleCopyFormatted = useCallback(async () => {
    const html = savedContentRef.current || "";
    const ok = await copyNoteFormatted(html);
    setShareOpen(false);
    toast(ok
      ? { title: "Copied", description: "Note copied with formatting." }
      : { variant: "destructive", title: "Copy failed", description: "Unable to copy to clipboard." }
    );
  }, [toast]);

  useEffect(() => {
    if (!isEditing) return;
    const interval = setInterval(() => {
      fetch(`${baseUrl}/lock`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...getCsrfHeaders() },
      }).catch(() => {});
    }, LOCK_HEARTBEAT_INTERVAL);
    return () => clearInterval(interval);
  }, [isEditing, baseUrl]);

  useEffect(() => {
    return () => {
      if (isEditingRef.current) {
        fetch(`${baseUrl}/lock`, {
          method: "DELETE",
          credentials: "include",
          headers: getCsrfHeaders(),
        }).catch(() => {});
      }
    };
  }, [baseUrl]);

  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      if (savedStatusTimerRef.current) clearTimeout(savedStatusTimerRef.current);
    };
  }, []);

  if (isLoading) {
    return (
      <div className="space-y-2" data-testid="entity-notes-loading">
        {!hideLabel && (
          <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1">
            <StickyNote className="w-3 h-3" /> Notes
          </Label>
        )}
        <Skeleton className="h-[140px] w-full" />
      </div>
    );
  }

  const note = data?.note ?? null;
  const currentContent = note?.content ?? "";
  const noteHasContent = hasContent(currentContent);

  const lockedByOther = note?.lockedBy && note.lockedBy.id !== currentUserId;
  const lockHolderName = note?.lockedBy?.username ?? "another user";

  if (isEditing) {
    return (
      <>
        <div className="flex flex-col h-full gap-2" data-testid="entity-notes-section">
          {!hideLabel && (
            <div className="flex items-center gap-3">
              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                <StickyNote className="w-3 h-3" /> Notes
              </Label>
            </div>
          )}

          <div className="flex-1 min-h-0 flex flex-col">
          <NoteEditor
            content={currentContent}
            onUpdate={handleUpdate}
            editable
            actions={
              <>
                {isDirty && pendingContent !== null && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleManualSave}
                    disabled={saveMutation.isPending}
                    data-testid="button-save-note"
                  >
                    <CloudUpload className="w-3.5 h-3.5 sm:mr-1" />
                    <span className="hidden sm:inline">Save</span>
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={handleDone}
                  disabled={saveMutation.isPending}
                  data-testid="button-done-note-edit"
                >
                  <Check className="w-3.5 h-3.5 sm:mr-1" />
                  <span className="hidden sm:inline">Done</span>
                </Button>
                {onExpand && (
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={handleExpandRequest}
                    aria-label="View notes full size"
                    data-testid="button-notes-expand-editing"
                  >
                    <Maximize2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </>
            }
          />
          </div>

          <div className="flex items-center justify-between gap-2">
            {note?.updatedBy && note.updatedAt ? (
              <p className="text-xs text-muted-foreground" data-testid="text-note-last-edited">
                Last edited by {note.updatedBy.username}, {formatTimeAgo(note.updatedAt)}
              </p>
            ) : <span />}
            <SaveStatusIndicator status={saveStatus} />
          </div>
        </div>

        <AlertDialog open={navGuardTarget !== null} onOpenChange={(open) => { if (!open) handleNavCancel(); }}>
          <AlertDialogContent data-testid="dialog-unsaved-changes">
            <AlertDialogHeader>
              <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
              <AlertDialogDescription>
                You have unsaved changes to your notes. If you leave now, those changes will be lost.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="button-stay-editing">Stay</AlertDialogCancel>
              <AlertDialogAction onClick={handleNavConfirm} data-testid="button-discard-changes">
                Discard & Leave
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  }

  return (
    <div className="flex flex-col h-full gap-2" data-testid="entity-notes-section">
      {!hideLabel && (
        <div className="flex items-center gap-2 flex-wrap">
          <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1">
            <StickyNote className="w-3 h-3" /> Notes
          </Label>
        </div>
      )}

      {lockedByOther && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 rounded-md px-3 py-2" data-testid="note-lock-indicator">
          <Lock className="w-3.5 h-3.5 shrink-0" />
          <span>Currently being edited by {lockHolderName}</span>
        </div>
      )}

      {/* Wrapper gives position context for the floating icons.
          It is NOT clickable — the clickable area and the icons are siblings inside it. */}
      <div className="relative flex-1 min-h-0">

        {/* Icons: sibling of the clickable area so their clicks never reach it */}
        {noteHasContent && (
          <div className="absolute top-[9px] right-[9px] z-10 flex items-center gap-2.5">
            {shareLabel && (
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setShareOpen(true)}
                aria-label="Share note"
                data-testid="button-notes-share"
              >
                <Share className="h-4 w-4" />
              </button>
            )}
            {onExpand && (
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground transition-colors"
                onClick={handleExpandRequest}
                aria-label="View notes full size"
                data-testid="button-notes-expand"
              >
                <Maximize2 className="h-4 w-4" />
              </button>
            )}
            {canEdit && !lockedByOther && (
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground transition-colors"
                onClick={handleStartEditing}
                disabled={lockMutation.isPending}
                aria-label="Edit note"
                data-testid="button-edit-note"
              >
                <Pencil className="h-4 w-4" />
              </button>
            )}
          </div>
        )}

        {/* Clickable preview area — no icons inside, so no propagation issues */}
        <div
          className={`h-full overflow-y-auto rounded-md${canEdit && !lockedByOther ? " cursor-pointer" : ""}`}
          onClick={canEdit && !lockedByOther && !lockMutation.isPending ? handleStartEditing : undefined}
          data-testid="note-preview-area"
        >
          {noteHasContent ? (
            <div className="h-full" data-testid="note-rendered-content">
              <NoteEditor content={currentContent} editable={false} />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center gap-3 h-full min-h-[120px]" data-testid="note-empty-placeholder">
              <p className="text-sm text-muted-foreground">No notes yet.</p>
              {canEdit && !lockedByOther && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleStartEditing}
                  disabled={lockMutation.isPending}
                  data-testid="button-add-note"
                >
                  Add Notes
                </Button>
              )}
            </div>
          )}
        </div>

      </div>

      {note?.updatedBy && note.updatedAt && (
        <p className="text-xs text-muted-foreground" data-testid="text-note-last-edited">
          Last edited by {note.updatedBy.username}, {formatTimeAgo(note.updatedAt)}
        </p>
      )}

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="max-w-sm" data-testid="dialog-share-note">
          <DialogHeader>
            <DialogTitle>Export Note</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-2 pt-1">
            <Button
              variant="outline"
              className="justify-start gap-2"
              onClick={handleCopyFormatted}
              data-testid="button-note-copy-formatted"
            >
              <Type className="w-4 h-4" />
              Copy formatted text
            </Button>
            <Button
              variant="outline"
              className="justify-start gap-2"
              onClick={handleCopyMarkdown}
              data-testid="button-note-copy-markdown"
            >
              <Clipboard className="w-4 h-4" />
              Copy as Markdown
            </Button>
            <Button
              variant="outline"
              className="justify-start gap-2"
              onClick={handleDownloadPdf}
              data-testid="button-note-download-pdf"
            >
              <FileDown className="w-4 h-4" />
              Download PDF
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
